<template>
  <div class="po">
    <div class="po-sub">displaying Part # / Part Qty / # of Orders</div>
    <div class="po-searchwrap">
      <input class="po-search" type="text" v-model="search" placeholder="Search part # or description…" />
      <button v-if="search" type="button" class="po-clear" @click="search = ''" title="Clear">×</button>
    </div>
    <div class="po-list" :class="{ 'po-loading': loading }">
      <div v-for="p in filtered" :key="p.partNumber" class="po-row"
           @click="$emit('select', p.partNumber)"
           :title="'Click to add this part to the filter\n' + (p.partNumber || '') + ' — ' + (p.partDescription || '')">
        <div class="po-part">{{ p.partNumber }}</div>
        <div class="po-bartrack">
          <template v-if="(segMap[p.partNumber] || []).length">
            <!-- Segments are an at-a-glance rep composition (colored + hover detail). They no longer intercept the
                 click — the whole row selects the PART (this card's purpose), so a part chip is always created;
                 rep filtering lives in the Sales Reps card. -->
            <div v-for="seg in segMap[p.partNumber]" :key="seg.rep" class="po-seg"
                 :style="{ width: seg.pct, background: segColor(seg.rep) }"
                 :title="seg.rep + ' — ' + fmtNum(seg.qty) + ' qty · ' + fmtNum(seg.orderCount) + ' orders · $' + fmtNum(seg.sales)"></div>
          </template>
          <div v-else class="po-bar" :style="{ width: '100%', background: barColor || '#16a085' }"></div>
          <span class="po-qty">{{ fmtNum(p.qty) }}</span>
        </div>
        <div class="po-orders">{{ fmtNum(p.orderCount) }}</div>
      </div>
      <!-- FOUND ON THE SERVER. This card holds the top 500 parts by quantity; these came back from a
           search over every part in the current filters, so they are listed BELOW the loaded rows and
           marked, rather than being silently absent. Clicking one filters exactly like a local row. -->
      <template v-if="remoteExtra.length">
        <div class="po-divider">
          found by searching every part in this selection
          <!-- remoteExtra is the count AFTER removing rows already on screen, so it is not what the
               server cut at. Say the cut, not the survivors. -->
          <span v-if="remoteCapped">(first 200 — narrow the filters to see the rest)</span>
        </div>
        <div v-for="p in remoteExtra" :key="'r' + p.partNumber" class="po-row po-row--remote"
             @click="$emit('select', p.partNumber)"
             :title="(p.partNumber || '') + ' \u2014 ' + (p.partDescription || '') + '\n\nNot in the top 500 by quantity, so it is not in the list above.\nClick to add it to the filter.'">
          <div class="po-part">{{ p.partNumber }}</div>
          <div class="po-bartrack">
            <div class="po-bar po-bar--remote" :style="{ width: '100%' }"></div>
            <span class="po-qty">{{ fmtNum(p.qty) }}</span>
          </div>
          <div class="po-orders">{{ fmtNum(p.orderCount) }}</div>
        </div>
      </template>
      <!-- The old text here was a flat "No parts match." on a card that had only ever been sent the top
           500 parts by quantity. A rep searching YEX read it as "never ordered" when the truth was 147
           YEX parts across 525 order lines, none of them in the top 500. Never report that nothing
           matched without saying what was searched. -->
      <div v-if="searchBusy" class="po-empty">Searching every part in this selection…</div>
      <!-- Selecting a found part chips it, and a chipped part drops out of BOTH lists -- so without this
           branch the card announced "no part matching X was ordered" about the very part the rep had just
           clicked, with its chip visible directly above. -->
      <!-- Guarded on all three counts. Unguarded it sat AHEAD of the failure branch below, so a chip
           whose text merely contains the typed term hid "the part search could not be reached" -- the
           safety net this same fix had just added -- and printed "already selected" underneath a list of
           unselected parts that were still on screen. -->
      <div v-else-if="selectedMatches.length && !remoteFailed && !filtered.length && !remoteExtra.length" class="po-empty">
        “{{ searchTerm }}” is already selected as a filter above.
      </div>
      <div v-else-if="!filtered.length && !remoteExtra.length && searchTerm.length >= 2 && resultsAreForTerm" class="po-empty">
        <template v-if="remoteFailed">The part search could not be reached, so nothing here means nothing was searched — try again.</template>
        <template v-else-if="remoteDescriptionSearched">No part matching “{{ searchTerm }}” was ordered under the current filters. Every part in this selection was searched, not just the {{ fmtNum((parts || []).length) }} shown.</template>
        <!-- Below three characters the server searches part NUMBERS only, so claiming everything was
             searched would be false: "HP" is in no part number and in 102 parts by description. -->
        <template v-else>No part <b>number</b> matching “{{ searchTerm }}” was ordered under the current filters. Descriptions are searched from three characters — add one more.</template>
      </div>
      <div v-else-if="!filtered.length && !remoteExtra.length" class="po-empty">No parts match.</div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PartsOrdered",
  props: {
    parts: { type: Array, default: () => [] },
    selected: { type: Array, default: () => [] },   // currently-selected part #s (multi)
    // Optional bar tint: the report passes the active territory's color when exactly one territory is
    // in play, so the part bars match the rest of the cards. Falls back to the default green.
    barColor: { type: String, default: null },
    // Per-part sales-rep breakdown [{partNumber, salesRep, qty, orderCount, sales}] -> the bar renders
    // as stacked segments colored by each rep's territory, with per-rep detail on hover.
    partReps: { type: Array, default: () => [] },
    repColorMap: { type: Object, default: null },
    loading: { type: Boolean, default: false },   // a filter reload is in progress → sweep a "processing" line across the rows
    // Rows the SERVER found for the current term, in this card's own row shape.
    remote: { type: Array, default: () => [] },
    remoteTerm: { type: String, default: "" },
    remoteBusy: { type: Boolean, default: false },
    remoteCapped: { type: Boolean, default: false },
    // Whether the server included part DESCRIPTIONS in this search (it does so from 3 characters).
    // The card's wording depends on it, so it comes from the response rather than being inferred here.
    remoteDescriptionSearched: { type: Boolean, default: true },
    remoteFailed: { type: Boolean, default: false },
  },
  data() { return { search: "" }; },
  watch: {
    // Debounced so a fast typist fires one query, not eight. The PARENT fetches (it owns the filter
    // params); this card only ever reports what was typed.
    search() { this.emitSearch(false); },
    // A filter change reloads `parts` and invalidates server results for the SAME term -- they were
    // computed under the old filters -- so ask again rather than leave a stale row on screen.
    parts() { if (String(this.search || "").trim().length >= 2) this.emitSearch(true); },
  },
  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase();
      const sel = this.selected || [];
      // Selected parts move to chips above and drop out of the list.
      const list = (this.parts || []).filter((p) => sel.indexOf(p.partNumber) < 0);
      if (!q) return list;
      return list.filter(
        (p) =>
          (p.partNumber || "").toLowerCase().indexOf(q) >= 0 ||
          (p.partDescription || "").toLowerCase().indexOf(q) >= 0
      );
    },
    // The term the DISPLAYED results belong to. Using the live input would flash "nothing matched" for
    // a term whose query has not come back yet.
    searchTerm() { return String(this.search || "").trim(); },
    searchBusy() { return this.remoteBusy && this.searchTerm.length >= 2; },
    // Server rows minus anything already on screen or already chipped. Shown only once the results
    // belong to what is currently typed, so a stale response cannot surface under a newer term.
    remoteExtra() {
      const q = this.searchTerm.toLowerCase();
      if (q.length < 2) return [];
      if (String(this.remoteTerm || "").trim().toLowerCase() !== q) return [];
      const sel = (this.selected || []).map((x) => String(x).toLowerCase());
      const shown = {};
      (this.filtered || []).forEach((p) => { shown[String(p.partNumber || "").toLowerCase()] = 1; });
      return (this.remote || []).filter((p) => {
        const k = String(p.partNumber || "").toLowerCase();
        return k && !shown[k] && sel.indexOf(k) < 0;
      });
    },
    // Do the results on screen actually belong to what is typed? Without this the "nothing matched"
    // sentence flashed for up to 250ms on every keystroke, while the previous term's answer was still
    // the only one in hand.
    resultsAreForTerm() {
      return String(this.remoteTerm || "").trim().toLowerCase() === this.searchTerm.toLowerCase();
    },
    selectedMatches() {
      const q = this.searchTerm.toLowerCase();
      if (q.length < 2) return [];
      return (this.selected || []).filter((s) => String(s).toLowerCase().indexOf(q) >= 0);
    },
    // Scale bars to the largest qty in the FULL set (stable while searching).
    maxQty() { return (this.parts || []).reduce((m, p) => Math.max(m, Number(p.qty) || 0), 0); },
    // partNumber -> [{rep, qty, orderCount, sales}] (qty desc) for the stacked, territory-colored bars.
    segMap() {
      var m = {};
      (this.partReps || []).forEach(function (pr) {
        if (!pr || !pr.partNumber) return;
        if (!m[pr.partNumber]) m[pr.partNumber] = [];
        m[pr.partNumber].push({ rep: pr.salesRep, qty: Number(pr.qty) || 0, orderCount: Number(pr.orderCount) || 0, sales: Number(pr.sales) || 0 });
      });
      // Each part's segments are sized as a SHARE of that part's OWN total, so the colored bar fills
      // the entire track (a 100% composition bar). Row order (by part qty) is unchanged.
      Object.keys(m).forEach(function (k) {
        var segs = m[k];
        segs.sort(function (a, b) { return b.qty - a.qty; });
        var total = segs.reduce(function (s, x) { return s + x.qty; }, 0);
        segs.forEach(function (x) { x.pct = total > 0 ? (x.qty / total * 100).toFixed(2) + "%" : "0%"; });
      });
      return m;
    },
  },
  methods: {
    barPct(v) {
      const m = this.maxQty;
      if (m <= 0) return "0%";
      return Math.max(3, (Number(v) || 0) / m * 100).toFixed(1) + "%";
    },
    // Segment width: raw proportion of the busiest part's qty (no min floor, so many small reps
    // don't visually overflow). A part's segments sum to that part's whole-bar width.
    segPct(v) {
      const m = this.maxQty;
      if (m <= 0) return "0%";
      return ((Number(v) || 0) / m * 100).toFixed(2) + "%";
    },
    segColor(rep) {
      if (this.repColorMap && this.repColorMap[rep || ""]) return this.repColorMap[rep || ""];
      return this.barColor || "#16a085";
    },
    emitSearch(immediate) {
      const self = this;
      clearTimeout(this._searchTimer);
      const go = function () { self.$emit("search", String(self.search || "").trim()); };
      if (immediate) { go(); return; }
      this._searchTimer = setTimeout(go, 250);
    },
    fmtNum(n) { return (Number(n) || 0).toLocaleString("en-US"); },
  },
};
</script>

<style scoped>
.po { display: flex; flex-direction: column; min-height: 0; flex: 1 1 auto; }
.po-sub { font-size: 11px; color: #777; padding: 0 14px 6px; }
.po-searchwrap { position: relative; padding: 0 14px 8px; }
.po-search { width: 100%; font-size: 12px; padding: 5px 24px 5px 8px; border: 1px solid #d6d6d6; border-radius: 6px; }
.po-search:focus { outline: none; border-color: #9e2436; }
.po-clear { position: absolute; right: 20px; top: 3px; border: 0; background: none; color: #999; font-size: 16px; cursor: pointer; line-height: 1; }
/* Fill the card and scroll INTERNALLY only (one styled scrollbar — no second card-body bar); the cap keeps
   ~10 parts in view, then the scrollbar reveals the rest. flex + min-height:0 also lets it shrink on a short
   card without spawning the outer scrollbar. */
.po-list { flex: 1 1 auto; min-height: 0; overflow-y: auto; max-height: 232px; padding: 0 14px 4px; }
.po-row { display: grid; grid-template-columns: 84px 1fr 38px; align-items: center; gap: 6px; margin-bottom: 4px; cursor: pointer; border-radius: 4px; padding: 1px 3px; }
.po-row:hover { background: #f3f4f6; }
.po-row--sel { background: #fdecee; box-shadow: inset 0 0 0 1px #9e2436; }
.po-part { font-size: 11px; color: #444; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.po-bartrack { position: relative; background: #eef0f1; border-radius: 3px; height: 16px; overflow: hidden; display: flex; align-items: stretch; }
.po-bar { height: 100%; background: #16a085; border-radius: 3px; min-width: 3px; transition: width .3s ease; }
.po-seg { height: 100%; min-width: 2px; flex: 0 0 auto; transition: width .3s ease; cursor: pointer; }
.po-seg:hover { filter: brightness(1.12); }
.po-qty { position: absolute; left: 6px; top: 0; line-height: 16px; font-size: 10px; font-weight: 600; color: #fff; text-shadow: 0 0 3px rgba(0,0,0,.55); white-space: nowrap; }
.po-orders { font-size: 10.5px; color: #555; text-align: right; font-variant-numeric: tabular-nums; }
.po-empty { padding: 16px; text-align: center; color: #999; font-size: 12px; line-height: 1.5; }
.po-divider { margin: 8px 0 4px; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #8a94a0; border-top: 1px dashed #dfe3e8; padding-top: 5px; }
.po-row--remote .po-part { font-style: italic; }
.po-bar--remote { background: #7f8c9b !important; }
html.dark-mode .po-divider { color: #8f9aa6; border-top-color: #3a444e; }
/* "Processing" sweep while a filter reloads (rows stay visible, dimmed). */
.po-loading .po-row { position: relative; opacity: .85; }
.po-loading .po-row::after { content: ""; position: absolute; left: 0; right: 0; top: 50%; height: 2px; margin-top: -1px; pointer-events: none; background: linear-gradient(90deg, rgba(158,36,54,0) 0%, rgba(158,36,54,0) 42%, #9e2436 50%, rgba(158,36,54,0) 58%, rgba(158,36,54,0) 100%); background-size: 230% 100%; animation: po-sweep 1.15s linear infinite; z-index: 2; }
@keyframes po-sweep { from { background-position: 130% 0; } to { background-position: -130% 0; } }
</style>
